import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useProducts = () => {
  const { message } = App.useApp();
  const { token } = useStateContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ productToPurchaseRequests, setProductToPurchaseRequests] = useState([]);
  const [productsInPurchaseStatuses, setProductsInPurchaseStatuses] = useState([]);
  const [organizedOferte, setOrganizedOferte] = useState([]);
  const [productStatusOptions, setProductStatusOptions] = useState([]);
  const [overallSummary, setOverallSummary] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20, // Reduc pentru că acum paginăm ofertele, nu produsele
    total: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    product_id: null,
    manager_id: null,
    client_name: '',
    product_statuses: [4,5,6], // 4=selectează furnizor, 5=negociere furnizor, 6=comandat
    product_in_purchase_statuses: [1,2,3,4,5,6,7,8,9]
  });

  

  

  
  // Funcție pentru aplicarea filtrelor
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 })); // Resetăm la prima pagină
  }, []);



  // Funcția principală care înlocuiește fetchProducts pentru DecideFurnizori
const fetchOrganizedData = useCallback(async () => {
  if (!token) return;

  setLoading(true);

  try {
    const params = new URLSearchParams({
      token,
      page: pagination.current.toString(),
      page_size: pagination.pageSize.toString()
    });

    // Adăugăm filtrele
    if (filters.search) params.append('search', filters.search);
    if (filters.product_id) params.append('product_id', filters.product_id.toString());
    if (filters.manager_id) params.append('manager_id', filters.manager_id.toString());
    if (filters.client_name) params.append('client_name', filters.client_name);
    if (filters.product_statuses && filters.product_statuses.length > 0) {
      params.append('product_statuses', filters.product_statuses.toString());
    }
    if (filters.product_in_purchase_statuses && filters.product_in_purchase_statuses.length > 0) {
      params.append('product_in_purchase_statuses', filters.product_in_purchase_statuses.toString());
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}/get_organized_products_data?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      
      // Setăm datele organizate
      setOrganizedOferte(data.oferte || []);
      setProductsInPurchaseStatuses(data.purchase_statuses || []);
      setProductStatusOptions(data.product_status_options || []);
      setOverallSummary(data.summary || null);
      
      // Actualizăm paginația
      setPagination(prev => ({
        ...prev,
        current: data.pagination?.current || 1,
        total: data.pagination?.total || 0,
        pageSize: data.pagination?.pageSize || prev.pageSize
      }));

      // Pentru compatibilitate, setăm și products cu toate produsele din toate ofertele
      const allProducts = [];
      data.oferte?.forEach(oferta => {
        oferta.products?.forEach(product => {
          allProducts.push({
            ...product,
            oferta: oferta.oferta_info
          });
        });
      });
      setProducts(allProducts);

    } else {
      message.error('Eroare la încărcarea datelor');
    }
  } catch (error) {
    console.error('Eroare la fetch date:', error);
    message.error('Eroare de conexiune');
  } finally {
    setLoading(false);
  }
}, [token, pagination.current, pagination.pageSize, filters]);




  const changeBoughtPriceForProductOffer = useCallback(async (offer_id, product_offer_id, new_bought_price) => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${offer_id}/produse/${product_offer_id}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          bought_price: new_bought_price,
         })
      });

      if (response.ok) {
        // Actualizare locală în organizedOferte
        setOrganizedOferte(prevOferte => 
          prevOferte.map(oferta => ({
            ...oferta,
            products: oferta.products.map(product => 
              product.id === product_offer_id 
                ? { ...product, bought_price: new_bought_price }
                : product
            )
          }))
        );
        return true;
      } else {
        message.error('Eroare la actualizarea prețului de achiziție');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea prețului de achiziție:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  // Funcție pentru actualizarea locală a prețului unui produs
  const updateProductPrice = useCallback((productId, newBoughtPrice) => {
    setProducts(prevProducts => 
      prevProducts.map(product => 
        product.id === productId 
          ? { ...product, bought_price: newBoughtPrice }
          : product
      )
    );

    // Actualizăm și în organizedOferte
    setOrganizedOferte(prevOferte => 
      prevOferte.map(oferta => ({
        ...oferta,
        products: oferta.products.map(product => 
          product.id === productId 
            ? { ...product, bought_price: newBoughtPrice }
            : product
        )
      }))
    );
  }, []);


  
  const deleteProductFromPurchaseRequest = useCallback(async (product_request_id) => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/purchase-requests/produse/${product_request_id}?token=${token}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Găsim produsul pentru a obține oferta_id și produs_to_oferta_id
        let ofertaId = null;
        let produsToOfertaId = null;
        
        setOrganizedOferte(prevOferte => {
          const updatedOferte = prevOferte.map(oferta => ({
            ...oferta,
            products: oferta.products.map(product => {
              if (product.purchase_info?.id === product_request_id) {
                ofertaId = oferta.oferta_id;
                produsToOfertaId = product.id;
                return { 
                  ...product, 
                  purchase_info: null,
                  product_status_id: 4 // Resetăm statusul la "selectează furnizor"
                };
              }
              return product;
            })
          }));
          return updatedOferte;
        });

        // Actualizăm statusul produsului în backend
        if (ofertaId && produsToOfertaId) {
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}/produse/${produsToOfertaId}?token=${token}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ 
                product_status_id: 4 // "selectează furnizor"
              })
            });
          } catch (statusError) {
            console.warn('Eroare la actualizarea statusului produsului în backend:', statusError);
            // Nu întrerupem fluxul dacă actualizarea statusului eșuează
          }
        }
        
        return true;
      } else {
        // Încercăm să extragem mesajul de eroare din răspuns
        let errorMessage = 'Eroare la ștergerea produsului din cerere';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          console.warn('Nu s-a putut parsa răspunsul de eroare:', parseError);
        }
        
        console.log('Eroare la ștergerea produsului din cerere:', response.status, errorMessage);
        message.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Eroare la ștergerea produsului din cerere:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  const addProductToPurchaseRequest = useCallback(async (oferta_id, produs_to_oferta_id, furnizor_id, furnizor_name, bought_price) => {
    console.log(oferta_id, produs_to_oferta_id, furnizor_id, furnizor_name)
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/purchase-requests/produse?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          oferta_id: oferta_id,
          produs_to_oferta_id: produs_to_oferta_id,
          furnizor_id: furnizor_id,
          furnizor_name: furnizor_name
        })
      });

      if (response.ok) {
        const newPurchaseRequest = await response.json();
        
        // Actualizare locală - adăugăm purchase_info la produs și schimbăm statusul la "negociere furnizor" (5)
        setOrganizedOferte(prevOferte => 
          prevOferte.map(oferta => ({
            ...oferta,
            products: oferta.products.map(product => 
              product.id === produs_to_oferta_id 
                ? { 
                    ...product, 
                    bought_price: bought_price,
                    product_status_id: 5, // Schimbăm statusul la "negociere furnizor"
                    purchase_info: {
                      id: newPurchaseRequest.id,
                      purchase_request_id: newPurchaseRequest.purchase_request_id,
                      produs_to_oferta_id: produs_to_oferta_id,
                      furnizor_name: furnizor_name,
                      furnizor_id: furnizor_id,
                      status_id: 1, // nou adăugat
                      date_added: new Date().toISOString(),
                      date_updated: new Date().toISOString()
                    }
                  }
                : product
            )
          }))
        );

        // Actualizăm prețul de achiziție
        await changeBoughtPriceForProductOffer(oferta_id, produs_to_oferta_id, bought_price);
        
        // Actualizăm statusul produsului în backend
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/oferte/${oferta_id}/produse/${produs_to_oferta_id}?token=${token}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              product_status_id: 5 // "negociere furnizor"
            })
          });
        } catch (statusError) {
          console.warn('Eroare la actualizarea statusului produsului în backend:', statusError);
          // Nu întrerupem fluxul dacă actualizarea statusului eșuează
        }
        
        return true;
      } else {
        message.error('Eroare la adăugarea produsului în cerere');
        return false;
      }
    } catch (error) {
      console.error('Eroare la adăugarea produsului în cerere:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token, changeBoughtPriceForProductOffer]);
  
  // Funcție pentru resetarea filtrelor
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      product_id: null,
      manager_id: null,
      client_name: '',
      product_statuses: [4,5,6], // 4=selectează furnizor, 5=negociere furnizor, 6=comandat
      product_in_purchase_statuses: [1,2,3,4,5,6,7]
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  // Funcție pentru schimbarea paginii
  const handleTableChange = useCallback((pagination) => {
    setPagination(pagination);
  }, []);
  

  const fetchProductsInPurchaseStatuses = useCallback(async () => {
    if (!token) {
      console.warn('⚠️ fetchProductsInPurchaseStatuses: No token available');
      return;
    }

    console.log('🔄 fetchProductsInPurchaseStatuses: Starting fetch...');

    try {
      const url = `${process.env.REACT_APP_API_URL}/get_products_in_purchase_statuses?token=${token}`;
      console.log('📊 fetchProductsInPurchaseStatuses: Making request to:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ fetchProductsInPurchaseStatuses: Data received:', data.length, 'items');
        setProductsInPurchaseStatuses(data);
      } else {
        console.error('❌ fetchProductsInPurchaseStatuses: HTTP error:', response.status, response.statusText);
        message.error('Eroare la încărcarea produselor în stoc');
      }
    } catch (error) {
      console.error('❌ fetchProductsInPurchaseStatuses: Network error:', error);
      message.error('Eroare la încărcarea produselor în stoc');
    }
  }, [token]);

  // Funcție pentru obținerea tuturor solicitărilor de achiziție
  const fetchProductToPurchaseRequests = useCallback(async () => {
    if (!token) {
      console.warn('⚠️ fetchProductToPurchaseRequests: No token available');
      return;
    }

    console.log('🔄 fetchProductToPurchaseRequests: Starting fetch...');

    try {
      // Folosim endpoint-ul generic pentru toate cererile de achiziție
      const url = `${process.env.REACT_APP_API_URL}/get_purchase_requests_grouped_by_furnizor?product_in_purchase_statuses=1,2,3,4,5,6,7&token=${token}`;
      console.log('🛒 fetchProductToPurchaseRequests: Making request to:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ fetchProductToPurchaseRequests: Data received:', data.length, 'items');
        setProductToPurchaseRequests(data);
      } else {
        console.error('❌ fetchProductToPurchaseRequests: HTTP error:', response.status, response.statusText);
        message.error('Eroare la încărcarea solicitărilor de achiziție');
      }
    } catch (error) {
      console.error('❌ fetchProductToPurchaseRequests: Network error:', error);
      message.error('Eroare la încărcarea solicitărilor de achiziție');
    }
  }, [token]);

  // Folosim fetchOrganizedData manual, nu automat
  // useEffect eliminat - se va apela manual din componente

  return {
    products,
    loading,
    pagination,
    productToPurchaseRequests,
    productsInPurchaseStatuses,
    organizedOferte,
    setOrganizedOferte,
    productStatusOptions,
    overallSummary,
    filters,  
    applyFilters,
    clearFilters,
    handleTableChange,
    changeBoughtPriceForProductOffer,
    updateProductPrice,
    addProductToPurchaseRequest,
    fetchProductsInPurchaseStatuses,
    fetchProductToPurchaseRequests,
    deleteProductFromPurchaseRequest,
    fetchOrganizedData
  };
}; 